/**
 * Brand color utilities for Hit components
 */

/**
 * Determines if a hex color is dark (for text contrast)
 * @param hex - Hex color string (e.g., '#FF0000' or 'FF0000')
 * @returns true if color is dark, false otherwise
 */
export function isDark(hex: string): boolean {
  if (!hex) return false;
  let c = hex.replace('#', '');
  if (c.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

/**
 * Determines if a hex color is white or near-white
 * @param hex - Hex color string (e.g., '#FFFFFF' or 'FFFFFF')
 * @returns true if color is white or near-white, false otherwise
 */
export function isWhiteOrNearWhite(hex: string): boolean {
  if (!hex) return false;
  let c = hex.replace('#', '');
  if (c.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return r >= 240 && g >= 240 && b >= 240;
}

/** Brand colour used for Työmarkkinatori jobs (details button, left border, etc.). */
export const TYOMARKKINATORI_PURPLE = '#6B46C1';

/**
 * Gets brand color styling for Hit components
 */
export interface BrandColorStyles {
  useBrandColour: boolean;
  leftBorderStyle: React.CSSProperties;
  detailsBtnStyle: React.CSSProperties;
  detailsBtnHoverStyle: React.CSSProperties;
  activePillClass: string;
  activePillStyle: React.CSSProperties;
}

/**
 * Calculates brand color styles for a hit
 * @param brandColour - The brand color hex string
 * @param isTyomarkkinatori - Whether this is a Tyomarkkinatori job
 * @returns Brand color styling configuration
 */
export function getBrandColorStyles(
  brandColour: string | undefined | null,
  isTyomarkkinatori: boolean | undefined
): BrandColorStyles {
  const DEFAULT_BRAND_GREEN = '#3B5E45'; // Matches jcThemeHex.light.cta

  const effectiveBrandColour = isTyomarkkinatori ? TYOMARKKINATORI_PURPLE : brandColour;
  const useBrandColour = effectiveBrandColour && !isWhiteOrNearWhite(effectiveBrandColour);
  
  const leftBorderStyle = useBrandColour ? { borderLeft: `4px solid ${effectiveBrandColour}` } : {};
  
  const detailsBtnStyle = useBrandColour
    ? {
        backgroundColor: effectiveBrandColour,
        color: isDark(effectiveBrandColour) ? '#fff' : '#111827',
        border: 'none',
      }
    : {
        backgroundColor: DEFAULT_BRAND_GREEN,
        color: '#fff',
        border: 'none',
      };
  
  const detailsBtnHoverStyle = useBrandColour
    ? {
        backgroundColor: effectiveBrandColour,
        filter: 'brightness(0.92)',
      }
    : {
        backgroundColor: DEFAULT_BRAND_GREEN,
        filter: 'brightness(0.92)',
      };
  
  const activePillClass = useBrandColour
    ? `rounded-full px-2 py-0.5 underline-offset-2`
    : 'rounded-full bg-green-100 text-green-800 px-2 py-0.5 underline-offset-2';
  
  const activePillStyle = useBrandColour
    ? {
        backgroundColor: effectiveBrandColour,
        color: isDark(effectiveBrandColour) ? '#fff' : '#111827',
      }
    : {
        backgroundColor: DEFAULT_BRAND_GREEN,
        color: '#fff',
      };
  
  return {
    useBrandColour,
    leftBorderStyle,
    detailsBtnStyle,
    detailsBtnHoverStyle,
    activePillClass,
    activePillStyle,
  };
}
